import React, { useState, useRef, useEffect } from 'react';
import { Description, PlayArrow, Pause } from '@material-ui/icons';
import { formatMessageContent } from '../../../utils/messageFormatters';

const AudioPlayer = ( { storageUrl, sender, duration: propDuration } ) => {
	const [ isPlaying, setIsPlaying ] = useState( false );
	const [ duration, setDuration ] = useState( 0 );
	const [ currentTime, setCurrentTime ] = useState( 0 );
	const [ isDragging, setIsDragging ] = useState( false );
	const audioRef = useRef( null );
	const progressRef = useRef( null );

	useEffect( () => {
		const handleMouseMove = ( e ) => {
			if ( !isDragging || !progressRef.current || !duration ) return;
			const rect = progressRef.current.getBoundingClientRect();
			const newX = e.clientX - rect.left;
			const newTime = Math.max( 0, Math.min( newX / rect.width, 1 ) ) * duration;
			setCurrentTime( newTime );
		};

		const handleMouseUp = () => {
			if ( !isDragging ) return;
			audioRef.current.currentTime = currentTime;
			setIsDragging( false );
		};

		window.addEventListener( 'mousemove', handleMouseMove );
		window.addEventListener( 'mouseup', handleMouseUp );

		return () => {
			window.removeEventListener( 'mousemove', handleMouseMove );
			window.removeEventListener( 'mouseup', handleMouseUp );
		};
	}, [ isDragging, duration, currentTime ] );

	const handlePlayPause = () => {
		if ( isPlaying ) {
			audioRef.current.pause();
		} else {
			audioRef.current.play();
		}
		setIsPlaying( !isPlaying );
	};

	const handleTimeUpdate = () => {
		if ( !isDragging ) {
			setCurrentTime( audioRef.current.currentTime );
		}
	};

	const handleLoadedMetadata = () => {
		const dur = audioRef.current.duration;
		setDuration( dur && dur !== Infinity ? dur : propDuration );
	};

	const formatTime = ( time ) => {
		const minutes = Math.floor( time / 60 );
		const seconds = Math.floor( time % 60 );
		return `${minutes}:${seconds.toString().padStart( 2, '0' )}`;
	};

	const handleSeekClick = ( e ) => {
		if ( !duration ) return;
		const rect = progressRef.current.getBoundingClientRect();
		const clickX = e.clientX - rect.left;
		const newTime = ( clickX / rect.width ) * duration;
		audioRef.current.currentTime = newTime;
		setCurrentTime( newTime );
	};

	const handleDragStart = ( e ) => {
		e.preventDefault();
		setIsDragging( true );
	};

	const progressPercent = duration && duration !== Infinity
		? ( currentTime / duration ) * 100
		: 0;

	const useProgress = storageUrl?.includes( 'storage' ) ? progressPercent : ( currentTime / ( propDuration || 1 ) ) * 100;

	return (
		<div style={{
			display      : 'flex',
			alignItems   : 'center',
			gap          : '8px',
			background   : 'rgba(134, 150, 160, 0.15)',
			padding      : '8px 12px',
			borderRadius : '8px',
			minWidth     : '200px',
			maxWidth     : '300px'
		}}>
			<audio
				ref={audioRef}
				src={storageUrl}
				onTimeUpdate={handleTimeUpdate}
				onLoadedMetadata={handleLoadedMetadata}
				onEnded={() => setIsPlaying( false )}
			/>
			<button
				onClick={handlePlayPause}
				style={{
					background     : 'none',
					border         : 'none',
					cursor         : 'pointer',
					padding        : '4px',
					display        : 'flex',
					alignItems     : 'center',
					justifyContent : 'center',
					color          : sender === 'sent' ? '#34b7f1' : '#8696a0'
				}}
			>
				{isPlaying ? <Pause /> : <PlayArrow />}
			</button>

			<div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
				<div
					ref={progressRef}
					onClick={handleSeekClick}
					style={{
						width        : '100%',
						height       : '6px',
						background   : 'rgba(134, 150, 160, 0.2)',
						borderRadius : '3px',
						position     : 'relative',
						cursor       : 'pointer',
						userSelect   : 'none'
					}}
				>
					{/* barra preenchida */}
					<div
						style={{
							position     : 'absolute',
							left         : 0,
							top          : 0,
							height       : '100%',
							width        : `${useProgress}%`,
							background   : sender === 'sent' ? '#34b7f1' : '#8696a0',
							borderRadius : '3px'
						}}
					/>
					{/* círculo arrastável */}
					<div
						onMouseDown={handleDragStart}
						style={{
							position        : 'absolute',
							top             : '50%',
							left            : `${useProgress}%`,
							transform       : 'translate(-50%, -50%)',
							width           : '12px',
							height          : '12px',
							backgroundColor : sender === 'sent' ? '#34b7f1' : '#8696a0',
							borderRadius    : '50%',
							boxShadow       : '0 0 2px rgba(0,0,0,0.3)',
							cursor          : 'pointer'
						}}
					/>
				</div>
				<div style={{
					display        : 'flex',
					justifyContent : 'space-between',
					fontSize       : '12px',
					color          : '#8696a0'
				}}>
					<span>{formatTime( currentTime )}</span>
					<span>{formatTime( duration ?? propDuration )}</span>
				</div>
			</div>
		</div>
	);
};

export default AudioPlayer;


const MediaMessage = ( { mediaType, storageUrl, fileName, mimetype, content, mediaContent, sender, duration, onImageClick } ) => {
	const isImage = mediaType === 'image';
	const isDocument = mediaType === 'document';
	const isAudio = mediaType === 'audio';
	const isSticker = mediaType === 'sticker';

	const renderContent = () => {
		if ( !content ) return null;

		return (
			<p style={{
				whiteSpace : 'pre-wrap',
				marginTop  : '8px'
			}}>
				{formatMessageContent( content )}
			</p>
		);
	};

	if ( isImage ) {
		return (
			<div style={{ maxWidth: '300px', margin: '8px 0', backgroundColor: 'transparent' }}>
				<img
					src={storageUrl}
					alt="Imagem enviada"
					style={{
						width        : '100%',
						height       : 'auto',
						maxHeight    : '300px',
						objectFit    : 'contain',
						cursor       : 'pointer',
						borderRadius : '8px'
					}}
					onClick={() =>
						onImageClick
							? onImageClick( storageUrl )
							: window.open( storageUrl, '_blank' )
					}
				/>
				{renderContent()}
			</div>

		);
	}

	if ( isDocument ) {
		if ( storageUrl ) {
			return (
				<div style={{ maxWidth: '300px' }}>
					<a
						style={{ textDecoration: 'none', color: '#575757' }}
						href={storageUrl}
						download={fileName}
						target='_blank'
						rel='noopener noreferrer'
					>
						<div
							style={{
								display      : 'flex',
								alignItems   : 'center',
								gap          : '8px',
								background   : 'rgba(134, 150, 160, 0.15)',
								padding      : '8px 12px',
								borderRadius : '8px',
								cursor       : 'pointer',
								width        : '100%',
								border       : 'none',
								outline      : 'none',
								color        : 'inherit',
								font         : 'inherit',
								textAlign    : 'left',
							}}
						>
							<Description style={{ color: '#8696a0' }} />
							<div style={{
								overflow     : 'hidden',
								whiteSpace   : 'nowrap',
								textOverflow : 'ellipsis'
							}}>
								{fileName || 'Documento'}
							</div>
						</div>
					</a>
					{renderContent()}
				</div>
			);
		} else {
			return (
				<div style={{ maxWidth: '300px' }}>
					<div
						style={{
							display      : 'flex',
							alignItems   : 'center',
							gap          : '8px',
							background   : 'rgba(134, 150, 160, 0.15)',
							padding      : '8px 12px',
							borderRadius : '8px',
							cursor       : 'pointer',
							width        : '100%'
						}}
						onClick={() => {
							if ( mimetype && fileName ) {
								const link = document.createElement( 'a' );
								link.href = `data:${mimetype};base64,${mediaContent}`;
								link.download = fileName;
								document.body.appendChild( link );
								link.click();
								document.body.removeChild( link );
							}
						}}
					>
						<Description style={{ color: '#8696a0' }} />
						<div style={{
							overflow     : 'hidden',
							whiteSpace   : 'nowrap',
							textOverflow : 'ellipsis'
						}}>
							{fileName || 'Documento'}
						</div>
					</div>
					{renderContent()}
				</div>
			);
		}
	}

	if ( isAudio ) {
		if ( !storageUrl ) {
			return (
				<div style={{ maxWidth: '300px', padding: '8px 12px', background: 'rgba(134, 150, 160, 0.15)', borderRadius: '8px', color: '#8696a0', fontSize: '13px' }}>
					Áudio não disponível
				</div>
			);
		}
		return (
			<div style={{ maxWidth: '300px' }}>
				<AudioPlayer storageUrl={storageUrl} sender={sender} duration={duration} />
				{renderContent()}
			</div>
		);
	}

	if ( isSticker ) {
		return (
			<div style={{ maxWidth: '120px', margin: '8px 0', backgroundColor: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				<img
					src={storageUrl}
					alt="Sticker"
					style={{
						width        : 96,
						height       : 96,
						objectFit    : 'contain',
						borderRadius : 16,
						background   : 'transparent',
						display      : 'block',
					}}
				/>
			</div>
		);
	}

	return null;
};

export { MediaMessage };